/**
 * 未登録のBB ID を、名前が同じスタッフの旧IDとしてまとめて結び付ける(2026-10-12 発注者指示。D-207)。
 * 名前が同じスタッフがいる行だけを並べる。候補が1人(在籍が1人)の行は最初からチェック、2人以上の行は人を選ぶ。
 * 結び付けても過去の日次集計は変わらないので、取り込み直す期間(全体の最初〜最後)を出す。
 */
import { useMemo, useState } from 'react';
import Modal from '@core/components/Modal';
import type { Operator } from '@core/lib/types';
import { formatCount } from '@core/utils/formatters';
import { bulkLinkRows } from './unknownBbLink';
import type { UnknownBbId } from './unknownBbRepo';

export default function UnknownBbBulkModal({
  unknowns,
  operators,
  busy,
  onClose,
  onSubmit,
}: {
  unknowns: UnknownBbId[];
  operators: Operator[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (pairs: { unknown: UnknownBbId; operator: Operator }[]) => void;
}) {
  const rows = useMemo(() => bulkLinkRows(unknowns, operators), [unknowns, operators]);
  const [picked, setPicked] = useState<Record<string, string>>(() => Object.fromEntries(rows.map((r) => [r.unknown.bb_id, r.suggested?.id ?? ''])));
  const [checked, setChecked] = useState<Set<string>>(() => new Set(rows.filter((r) => r.suggested).map((r) => r.unknown.bb_id)));

  const pairs = rows
    .filter((r) => checked.has(r.unknown.bb_id))
    .map((r) => ({ unknown: r.unknown, operator: r.candidates.find((o) => o.id === picked[r.unknown.bb_id]) }))
    .filter((p): p is { unknown: UnknownBbId; operator: Operator } => !!p.operator);
  const from = pairs.map((p) => p.unknown.first_date).sort()[0];
  const to = pairs.map((p) => p.unknown.last_date).sort().at(-1);

  const toggle = (id: string, on: boolean) =>
    setChecked((cur) => {
      const next = new Set(cur);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <Modal onClose={busy ? () => undefined : onClose} width="min(860px, 94vw)">
      <h3 className="training-modal-title">名前が同じスタッフにまとめて結び付ける</h3>
      <p className="ui-hint">チェックした行の BB ID を、選んだスタッフの旧ID(無効)として足します。時給はその人の同じ種類のIDから写します。名前が同じスタッフが2人以上いる行は、人を選んでからチェックしてください。</p>
      {rows.length === 0 ? (
        <p className="ui-hint">名前が同じスタッフがいる行はありません。</p>
      ) : (
        <div className="report-scroll is-auto training-preview">
          <table className="report-table no-stack">
            <caption className="sr-only">結び付ける BB ID</caption>
            <thead>
              <tr>
                <th scope="col">結び付ける</th>
                <th scope="col">BB ID</th>
                <th scope="col">CSVの氏名</th>
                <th scope="col">スタッフ</th>
                <th scope="col">取込に出てきた日</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const id = r.unknown.bb_id;
                return (
                  <tr key={id}>
                    <td className="is-center">
                      <input type="checkbox" aria-label={`${id} を結び付ける`} checked={checked.has(id)} disabled={busy || !picked[id]} onChange={(e) => toggle(id, e.target.checked)} />
                    </td>
                    <td className="is-nowrap">{id}</td>
                    <td className="is-nowrap">{r.unknown.name || '—'}</td>
                    <td className="is-nowrap">
                      {r.candidates.length === 1 ? (
                        `${r.candidates[0]!.op_code} ${r.candidates[0]!.name}${r.candidates[0]!.status !== 'active' ? '(在籍以外)' : ''}`
                      ) : (
                        <select
                          className="ui-input"
                          aria-label={`${id} を結び付けるスタッフ`}
                          value={picked[id] ?? ''}
                          disabled={busy}
                          onChange={(e) => {
                            setPicked((cur) => ({ ...cur, [id]: e.target.value }));
                            toggle(id, !!e.target.value);
                          }}
                        >
                          <option value="">選ぶ({r.candidates.length}人)</option>
                          {r.candidates.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.op_code} {o.name}
                              {o.status !== 'active' ? '(在籍以外)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="is-nowrap">
                      {r.unknown.first_date} 〜 {r.unknown.last_date}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {pairs.length > 0 && (
        <p className="ui-hint">
          <b>
            過去の日の数字は、{from} 〜 {to} の OPレポート・対応履歴を取り込み直すと入ります。
          </b>
        </p>
      )}
      <div className="training-actions">
        <button type="button" className="ui-btn ui-btn-primary" disabled={busy || pairs.length === 0} onClick={() => onSubmit(pairs)}>
          {busy ? '結び付け中…' : `${formatCount(pairs.length)}件を結び付ける`}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose} disabled={busy}>
          やめる
        </button>
      </div>
    </Modal>
  );
}
